/**
 * About Page - Giới thiệu công ty
 * Thông tin người bán bắt buộc theo quy định cổng thanh toán
 */

import { UnifiedHeader } from '../../components/UnifiedHeader';
import { UnifiedFooter } from '../../components/UnifiedFooter';
import { SEOHead } from '../../components/SEOHead';
import { getCanonicalUrl, getSiteUrl } from '../../utils/siteConfig';
import { StructuredData } from '../../components/StructuredData';
import { Building2, Mail, Phone, MapPin, FileText } from 'lucide-react';
import { motion } from 'framer-motion';

export function AboutPage() {
  return (
    <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body transition-colors duration-200 min-h-screen flex flex-col'>
      <SEOHead
        title='Giới thiệu | Hướng nghiệp CAT'
        description='Thông tin về COGNOTIV VIETNAM - Đơn vị vận hành nền tảng Hướng nghiệp CAT'
        canonicalUrl={getCanonicalUrl('/gioi-thieu')}
      />
      <StructuredData
        data={{
          type: 'Organization',
          name: 'COGNOTIV VIETNAM',
          url: getSiteUrl(),
          logo: `${getSiteUrl()}/logo.png`,
          description: 'Nền tảng trắc nghiệm hướng nghiệp trực tuyến - Hướng nghiệp CAT',
        }}
      />
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Giới thiệu', url: getCanonicalUrl('/gioi-thieu') },
          ],
        }}
      />
      <UnifiedHeader />

      <motion.main
        className='flex-1 pt-20 pb-12'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          {/* Header */}
          <div className='text-center mb-12'>
            <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>Giới thiệu</h1>
            <p className='text-lg text-gray-600 dark:text-gray-300'>
              Thông tin về đơn vị vận hành nền tảng Hướng nghiệp CAT
            </p>
          </div>

          {/* Company Info */}
          <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-8'>
            <div className='flex items-center gap-3 mb-6'>
              <div className='w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center'>
                <Building2 className='w-6 h-6 text-blue-600 dark:text-blue-400' />
              </div>
              <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>Thông tin đơn vị</h2>
            </div>

            <div className='space-y-6'>
              <div>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>
                  Tên đơn vị
                </h3>
                <p className='text-gray-600 dark:text-gray-300'>
                  CÔNG TY CỔ PHẦN COGNOTIV VIỆT NAM
                </p>
              </div>

              <div className='grid md:grid-cols-2 gap-6'>
                <div className='flex items-start gap-3'>
                  <MapPin className='w-5 h-5 text-gray-400 mt-1 flex-shrink-0' />
                  <div>
                    <h4 className='font-medium text-gray-900 dark:text-white'>Địa chỉ</h4>
                    <p className='text-gray-600 dark:text-gray-300'>
                      Tầng 5, Tòa nhà Leadvisors Place, 41A Lý Thái Tổ, Phường Lý Thái Tổ, Quận Hoàn
                      Kiếm, Thành phố Hà Nội, Việt Nam
                    </p>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <Mail className='w-5 h-5 text-gray-400 mt-1 flex-shrink-0' />
                  <div>
                    <h4 className='font-medium text-gray-900 dark:text-white'>Email</h4>
                    <p className='text-gray-600 dark:text-gray-300'>
                      support@huongnghiep.cat.edu.vn
                    </p>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <Phone className='w-5 h-5 text-gray-400 mt-1 flex-shrink-0' />
                  <div>
                    <h4 className='font-medium text-gray-900 dark:text-white'>Điện thoại</h4>
                    <p className='text-gray-600 dark:text-gray-300'>0888307976</p>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <FileText className='w-5 h-5 text-gray-400 mt-1 flex-shrink-0' />
                  <div>
                    <h4 className='font-medium text-gray-900 dark:text-white'>Mã số thuế</h4>
                    <p className='text-gray-600 dark:text-gray-300'>0110808569</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* About Platform */}
          <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
            <h2 className='text-2xl font-bold text-gray-900 dark:text-white mb-6'>
              Về nền tảng Hướng nghiệp CAT
            </h2>

            <div className='prose dark:prose-invert max-w-none'>
              <p className='text-gray-600 dark:text-gray-300 leading-relaxed mb-4'>
                <strong>Hướng nghiệp CAT</strong> (Career Aptitude Test) là nền tảng trắc nghiệm
                hướng nghiệp trực tuyến được phát triển bởi COGNOTIV VIETNAM, kết hợp phương pháp
                đánh giá năng lực có bản quyền của IEQTE (Viện Khảo thí & Đánh giá Chất lượng Giáo
                dục).
              </p>

              <p className='text-gray-600 dark:text-gray-300 leading-relaxed mb-4'>
                Hướng nghiệp CAT mang đến giải pháp tự đánh giá sở trường toàn diện cho học sinh
                THPT với hình thức linh hoạt: lựa chọn bài đánh giá năng lực theo từng môn hoặc đánh
                giá tổng hợp cho cả 3 môn, kết hợp với công cụ tự đánh giá hướng nghiệp chuyên sâu.
                Dựa trên phương pháp phân tích khoa học đã được IEQTE đăng ký bản quyền, học sinh sẽ
                nhận được hồ sơ năng lực phản ánh rõ sở trường và năng lực học tập, đồng thời được
                tư vấn định hướng ngành học – nghề nghiệp phù hợp cho giai đoạn sau THPT.
              </p>

              <p className='text-gray-600 dark:text-gray-300 leading-relaxed mb-4'>
                Nền tảng cung cấp các bài thi thử tốt nghiệp THPT và trắc nghiệm hướng nghiệp dựa
                trên mô hình Holland RIASEC, giúp học sinh:
              </p>

              <ul className='list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2 mb-4'>
                <li>Khám phá năng lực và sở thích nghề nghiệp</li>
                <li>Định hướng lựa chọn ngành học phù hợp</li>
                <li>Luyện tập với đề thi thử THPT chất lượng</li>
                <li>Nhận báo cáo phân tích chi tiết về bản thân</li>
              </ul>

              <p className='text-gray-600 dark:text-gray-300 leading-relaxed'>
                Chúng tôi cam kết mang đến trải nghiệm hướng nghiệp khoa học, chính xác và hữu ích
                cho học sinh THPT trên toàn quốc.
              </p>
            </div>
          </div>
        </div>
      </motion.main>

      <UnifiedFooter />
    </div>
  );
}

export default AboutPage;
